import {useRef, useCallback} from 'react';

import {useSubscribeToNotificationsQuery} from '@dms/api/appsync';

const DEFAULT_TIMEOUT_MS = 30000;

const globalPendingFiles = new Map<
  string,
  {
    fileId: string;
    timeoutId: ReturnType<typeof setTimeout>;
    onSuccess?: () => void;
    onError?: () => void;
  }
>();

const globalCallbacks = new Map<
  string,
  {
    onSuccess?: () => void;
    onError?: () => void;
  }
>();

interface FileNotificationConfig {
  timeoutMs?: number;
  onTimeout?: (fileId: string) => void;
  onSuccess?: (fileId: string) => void;
  onError?: (fileId: string) => void;
}

export const useFileNotification = (config: FileNotificationConfig = {}) => {
  const TIMEOUT_MS = config.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  const pendingFilesRef = useRef(globalPendingFiles);
  const callbacksRef = useRef(globalCallbacks);

  const handleTimeout = useCallback(
    (fileId: string) => {
      pendingFilesRef.current.delete(fileId);
      callbacksRef.current.delete(fileId);
      config.onTimeout?.(fileId);
    },
    [config]
  );

  const handleNotificationInternal = useCallback(
    (fileId: string, status: string) => {
      const pending = pendingFilesRef.current.get(fileId);
      const callbacks = callbacksRef.current.get(fileId);

      if (!pending) {
        return false;
      }

      clearTimeout(pending.timeoutId);
      pendingFilesRef.current.delete(fileId);
      callbacksRef.current.delete(fileId);

      if (status === 'success') {
        config.onSuccess?.(fileId);
        callbacks?.onSuccess?.();
        return true;
      } else {
        config.onError?.(fileId);
        callbacks?.onError?.();
        return true;
      }
    },
    [config]
  );

  useSubscribeToNotificationsQuery((notification) => {
    if (notification.type !== 'file_storage-file_uploaded') {
      return;
    }

    const {fileId, fileStatus} = notification.payload;

    if (pendingFilesRef.current.has(fileId)) {
      handleNotificationInternal(fileId, fileStatus);
    }
  });

  const registerFile = useCallback(
    (
      fileId: string,
      callbacks?: {
        onSuccess?: () => void;
        onError?: () => void;
      }
    ) => {
      const existing = pendingFilesRef.current.get(fileId);
      if (existing) {
        clearTimeout(existing.timeoutId);
      }

      const timeoutId = setTimeout(() => {
        handleTimeout(fileId);
      }, TIMEOUT_MS);

      pendingFilesRef.current.set(fileId, {
        fileId,
        timeoutId,
        onSuccess: callbacks?.onSuccess,
        onError: callbacks?.onError,
      });

      if (callbacks) {
        callbacksRef.current.set(fileId, callbacks);
      }
    },
    [TIMEOUT_MS, handleTimeout]
  );

  const cancelFile = useCallback((fileId: string) => {
    const pending = pendingFilesRef.current.get(fileId);
    if (pending) {
      clearTimeout(pending.timeoutId);
      pendingFilesRef.current.delete(fileId);
      callbacksRef.current.delete(fileId);
    }
  }, []);

  return {
    registerFile,
    cancelFile,
  };
};
